import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TenantType, HostingTier } from '@/features/xroad/types/tenant.types';
import { useXRoadStore } from '@/features/xroad/store/xroad.store';

interface User {
  id: string;
  email: string;
  name: string;
  tenant: string;
  level: string;
  permissions?: UserPermissions;
  // X-Road Tenant Metadata
  tenantType: TenantType;
  hostingTier?: HostingTier | null;
  maxServices?: number | null;
  maxApiCallsPerMonth?: number | null;
}

interface UserPermissions {
  userInfo: {
    id: string;
    tenant: string;
    level: string;
  };
  permissions: {
    actions: ActionPermission[];
    dataScopes: Record<string, string>;
    fieldVisibility: Record<string, boolean>;
  };
}

interface ActionPermission {
  module: string;
  feature: string;
  hasAccess: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginTimestamp: number | null; // Track when user logged in

  // Actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
  setUser: (user: User) => void;
  setToken: (token: string, refreshToken?: string) => void;
  clearAuth: () => void;
  checkSessionExpiry: () => boolean; // Check if session is expired
}

// Session duration: 1 day in milliseconds
const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Temporary mock users with different tenant types (will be replaced by Keycloak)
// Each user represents a different organization type in the X-Road ecosystem
interface MockUser {
  username: string;
  password: string;
  data: User;
}

const TEMP_USERS: MockUser[] = [
  {
    username: 'senum',
    password: 'senum123',
    data: {
      id: '1',
      email: 'admin@senum.gouv.sn',
      name: 'SENUM Admin',
      tenant: 'senum',
      level: 'ADMIN',
      tenantType: TenantType.CENTRAL_AUTHORITY,
      hostingTier: null,
      maxServices: null,
      maxApiCallsPerMonth: null,
    },
  },
  {
    username: 'finance',
    password: 'finance123',
    data: {
      id: '2',
      email: 'admin@finances.gouv.sn',
      name: 'Administrateur MEF',
      tenant: 'min-finance',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_SELF_MANAGED,
      hostingTier: null,
      maxServices: null, // Unlimited
      maxApiCallsPerMonth: null, // Unlimited
    },
  },
  {
    username: 'education',
    password: 'education123',
    data: {
      id: '3',
      email: 'admin@education.gouv.sn',
      name: 'Administrateur MEN',
      tenant: 'min-education',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_HOSTED,
      hostingTier: HostingTier.STANDARD,
      maxServices: 20,
      maxApiCallsPerMonth: 100000,
    },
  },
  {
    username: 'enda',
    password: 'enda123',
    data: {
      id: '4',
      email: 'admin@endatiersmonde.org',
      name: 'Administrateur ENDA',
      tenant: 'ngo-enda',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_HOSTED_LIGHT,
      hostingTier: HostingTier.LIGHT,
      maxServices: 5,
      maxApiCallsPerMonth: 10000,
    },
  },
  {
    username: 'sante',
    password: 'sante123',
    data: {
      id: '5',
      email: 'admin@sante.gouv.sn',
      name: 'Administrateur ANACMU',
      tenant: 'min-sante',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_SELF_MANAGED,
      hostingTier: null,
      maxServices: null,
      maxApiCallsPerMonth: null,
    },
  },
  {
    username: 'interieur',
    password: 'interieur123',
    data: {
      id: '6',
      email: 'admin@interieur.gouv.sn',
      name: 'Administrateur ANEC',
      tenant: 'min-interieur',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_SELF_MANAGED,
      hostingTier: null,
      maxServices: null,
      maxApiCallsPerMonth: null,
    },
  },
  {
    username: 'digiboost',
    password: 'digiboost123',
    data: {
      id: '7',
      email: 'admin@digiboost.sn',
      name: 'Administrateur Digiboost',
      tenant: 'digiboost',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_SELF_MANAGED,
      hostingTier: null,
      maxServices: null,
      maxApiCallsPerMonth: null,
    },
  },
  {
    username: 'justice',
    password: 'justice123',
    data: {
      id: '8',
      email: 'admin@justice.gouv.sn',
      name: 'Administrateur MJUS',
      tenant: 'min-justice',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_HOSTED,
      hostingTier: HostingTier.STANDARD,
      maxServices: 20,
      maxApiCallsPerMonth: 100000,
    },
  },
  {
    username: 'culture',
    password: 'culture123',
    data: {
      id: '9',
      email: 'admin@culture.gouv.sn',
      name: 'Administrateur MCAT',
      tenant: 'min-culture',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_HOSTED,
      hostingTier: HostingTier.STANDARD,
      maxServices: 20,
      maxApiCallsPerMonth: 100000,
    },
  },
  {
    username: 'apix',
    password: 'apix123',
    data: {
      id: '10',
      email: 'admin@apix.sn',
      name: 'Administrateur APIX',
      tenant: 'apix',
      level: 'ADMIN',
      tenantType: TenantType.MEMBER_HOSTED_LIGHT,
      hostingTier: HostingTier.LIGHT,
      maxServices: 5,
      maxApiCallsPerMonth: 100000,
    },
  },
];

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      loginTimestamp: null,

      // Temporary frontend-only login (will be replaced by Keycloak)
      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        // Simulate async operation
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
          // Find matching user from mock users
          const mockUser = TEMP_USERS.find(
            u => u.username === username && u.password === password
          );

          if (!mockUser) {
            throw new Error('Invalid credentials');
          }

          const mockToken = `temp-token-${Date.now()}`;

          set({
            user: mockUser.data,
            token: mockToken,
            refreshToken: `temp-refresh-${Date.now()}`,
            isAuthenticated: true,
            isLoading: false,
            loginTimestamp: Date.now(),
            error: null,
          });

          // Sync tenant metadata with XRoadStore
          // This ensures the correct portal is displayed after login
          useXRoadStore.getState().setTenantMetadata({
            tenantId: mockUser.data.tenant,
            tenantName: mockUser.data.name.replace('Administrateur ', '').replace('Admin', ''),
            tenantType: mockUser.data.tenantType,
            hostingTier: mockUser.data.hostingTier,
            maxServices: mockUser.data.maxServices,
            maxApiCallsPerMonth: mockUser.data.maxApiCallsPerMonth,
            isActive: true,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false,
            isAuthenticated: false,
            user: null,
            token: null,
            refreshToken: null,
            loginTimestamp: null,
          });
          throw error;
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          loginTimestamp: null,
        });

        // Reset XRoad store to default (SENUM)
        useXRoadStore.getState().setTenantMetadata({
          tenantId: 'senum',
          tenantName: 'SENUM - Sénégal Numérique',
          tenantType: TenantType.CENTRAL_AUTHORITY,
          hostingTier: null,
          maxServices: null,
          maxApiCallsPerMonth: null,
          isActive: true,
        });
      },

      // Not used in temporary mode - will be used with Keycloak
      refreshAuth: async () => {
        // Placeholder for Keycloak integration
        return;
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true });
      },

      setToken: (token: string, refreshToken?: string) => {
        set({
          token,
          refreshToken: refreshToken || get().refreshToken,
          isAuthenticated: true,
        });
      },

      clearAuth: () => {
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          error: null,
          loginTimestamp: null,
        });
      },

      // Check if the session has expired (1 day)
      checkSessionExpiry: () => {
        const { loginTimestamp, isAuthenticated } = get();

        if (!isAuthenticated || !loginTimestamp) {
          return false;
        }

        const now = Date.now();
        const sessionAge = now - loginTimestamp;

        if (sessionAge > SESSION_DURATION) {
          // Session expired, clear auth
          get().clearAuth();
          return true; // Expired
        }

        return false; // Not expired
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        loginTimestamp: state.loginTimestamp,
      }),
    }
  )
);

export default useAuthStore;